import React, { useEffect } from "react";
import {
  Eye,
  Building,
  Calendar,
  AlertCircle,
} from "lucide-react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchInsurancePolicies } from "../../store/slices/Insurance_Certificate/InsuranceCertificateSlice";
import type { AppDispatch, RootState } from "../../store/store";

const InsuranceCertificates: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { currentLanguage } = useSelector((state: RootState) => state.language);

  const translations = {
    az: {
      title: "Sığorta şəhadətnamələrim",
      subtitle: "Şəhadətnamələrinizi izləyin və idarə edin",
      loading: "Məlumatlar yüklənir...",
      errorTitle: "Xəta baş verdi",
      insuredPerson: "Sığortalı Şəxs:",
      startDate: "Başlama tarixi:",
      endDate: "Bitmə tarixi:",
      details: "Ətraflı",
      noCertificatesTitle: "Heç bir şəhadətnamə tapılmadı",
      noCertificatesDesc: "Hələlik sığorta şəhadətnaməniz yoxdur."
    },
    ru: {
      title: "Мои страховые полисы",
      subtitle: "Отслеживайте и управляйте своими полисами",
      loading: "Загрузка данных...",
      errorTitle: "Произошла ошибка",
      insuredPerson: "Застрахованное лицо:",
      startDate: "Дата начала:",
      endDate: "Дата окончания:",
      details: "Подробнее",
      noCertificatesTitle: "Полисы не найдены",
      noCertificatesDesc: "У вас пока нет страховых полисов."
    },
    en: {
      title: "My Insurance policies",
      subtitle: "Track and manage your policies",
      loading: "Loading data...",
      errorTitle: "An error occurred",
      insuredPerson: "Insured Person:",
      startDate: "Start date:",
      endDate: "End date:",
      details: "Details",
      noCertificatesTitle: "No certificates found",
      noCertificatesDesc: "You don't have any insurance policy yet."
    }
  };

  const t = translations[currentLanguage as keyof typeof translations];

  const { policies, loading, error } = useSelector(
    (state: RootState) => state.insurancePolicy
  );

  useEffect(() => {
    if (policies.length === 0) {
      dispatch(fetchInsurancePolicies());
    }
  }, [dispatch, policies.length]);

  const formatDate = (dateString: string): string => {
    return dateString.replace(/-/g, '.');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-colorpink mx-auto mb-4"></div>
          <p className="text-gray-600">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <div>
              <h3 className="font-semibold text-red-900">{t.errorTitle}</h3>
              <p className="text-sm text-red-700 mt-1">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-3xl font-semibold text-stone-800">
        {t.title}
      </h1>
      <p className="text-stone-600 mb-6 leading-relaxed text-base tracking-wide">
        {t.subtitle}
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {policies.map((policy) => {
          return (
            <div
              key={policy.policy_number}
              className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-lg text-gray-900 mb-2">
                    {policy.policy_number}
                  </h3>
                  <span className="text-sm text-gray-600">
                    {policy.policy_type}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-green-700">
                    {policy.status}
                  </span>
                </div>
              </div>

              <div className="mb-4 p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-600">{t.insuredPerson}</span>
                <span className="text-sm font-medium text-gray-900 ml-2">
                  {policy.insured_person_name}
                </span>
              </div>

              <div className="grid grid-cols-1 gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">{t.startDate}</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatDate(policy.start_date)}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-400" />
                  <span className="text-sm text-gray-600">{t.endDate}</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatDate(policy.end_date)}
                  </span>
                </div>
              </div>

              <div className="flex gap-2 pt-4 border-t border-gray-100">
                <Link
                  to={`/insurance-detail/${policy.policy_number}`}
                  state={{ policy }}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-2 text-sm rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50 transition-colors font-medium"
                >
                  <Eye className="w-4 h-4" />
                  {t.details}
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      {policies.length === 0 && !loading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12">
          <div className="text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t.noCertificatesTitle}
            </h3>
            <p className="text-gray-500">
              {t.noCertificatesDesc}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export { InsuranceCertificates as Index };